import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, input, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { PartnerDirectoryService, PartnerDoctorOption, PartnerOption, PartnerServiceType, PrescriptionRestrictionModel } from '@lib/guarantees/data-access';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, combineLatest, debounceTime, of, switchMap } from 'rxjs';

import { MatIcon } from '@angular/material/icon';

import { OptionListComponent } from '../components/common/option-list.component';
import { OptionRowDirective } from '../components/common/option-row.directive';
import { OVERLAY_POSITIONS, SEARCH_DEBOUNCE_MS, SearchFieldComponent, fieldWidth } from '../components/common/search-field.component';
import { PartnerPickerComponent } from './partner-picker.component';

/**
 * Where a line is to be delivered, narrowed by hand while it is being authored.
 *
 * Optional throughout: most lines are delivered wherever the member finds a slot, and only some are
 * sent to a particular partner — a continuing course with one clinic, or a doctor the member is
 * already under. The partner is the one part that must be named, because a doctor is only meaningful
 * underneath one; the doctor on top of it is what turns "this clinic" into "this person".
 *
 * A branch is not asked for here. The backend accepts one and it is carried through untouched where
 * a line already carries it, but naming a branch is a decision about the member's convenience on the
 * day, which is not what this block is for.
 */
@Component({
  selector: 'guarantees-provider-restriction',
  standalone: true,
  imports: [CdkConnectedOverlay, CdkOverlayOrigin, MatIcon, TranslatePipe, OptionListComponent, OptionRowDirective, PartnerPickerComponent, SearchFieldComponent],
  templateUrl: './provider-restriction.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProviderRestrictionComponent implements OnInit {
  service = input.required<PartnerServiceType>();
  /** What the line already carries, so re-opening it shows who it was narrowed to. */
  initial = input<PrescriptionRestrictionModel | undefined>(undefined);

  changed = output<PrescriptionRestrictionModel | undefined>();

  private readonly directory = inject(PartnerDirectoryService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly overlayPositions = OVERLAY_POSITIONS;

  private readonly doctorOrigin = viewChild('doctorOrigin', { read: CdkOverlayOrigin });

  protected readonly partner = signal<PartnerOption | undefined>(undefined);
  protected readonly doctor = signal<PartnerDoctorOption | undefined>(undefined);

  protected readonly doctorTerm = signal('');
  protected readonly doctors = signal<PartnerDoctorOption[]>([]);
  protected readonly loadingDoctors = signal(false);
  protected readonly doctorsOpen = signal(false);

  /**
   * Measured when the list opens rather than read off the element in the template: a field inside a
   * dialog still being laid out measures zero, which the overlay reads as no width at all.
   */
  protected readonly panelWidth = signal(0);

  protected readonly doctorDisplay = computed(() => this.doctor()?.fullName ?? '');

  constructor() {
    combineLatest([toObservable(this.partner), toObservable(this.doctorTerm)])
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        switchMap(([partner, term]) => {
          if (!partner) {
            return of<PartnerDoctorOption[]>([]);
          }

          this.loadingDoctors.set(true);

          return this.directory.searchDoctors(partner.id, term).pipe(catchError(() => of<PartnerDoctorOption[]>([])));
        }),
        takeUntilDestroyed()
      )
      .subscribe(doctors => {
        this.doctors.set(doctors);
        this.loadingDoctors.set(false);
        this.adoptInitialDoctor(doctors);
      });
  }

  /** Read here rather than in the constructor: an input has no value until Angular has bound it. */
  ngOnInit(): void {
    const initial = this.initial();
    if (!initial) {
      return;
    }

    // Named by id and nothing else, so who it is has to be asked for. Shown as a bare id it would
    // tell the manager the line is narrowed without telling them to whom, which is worse than blank.
    this.directory
      .getPartnerById(initial.partnerId)
      .pipe(
        catchError(() => of(undefined)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(partner => this.partner.set(partner ?? { id: initial.partnerId, name: '', integrationEnabled: false }));
  }

  protected choosePartner(partner: PartnerOption): void {
    this.partner.set(partner);

    // The doctor belonged to the partner that has just been replaced.
    this.doctor.set(undefined);
    this.doctorTerm.set('');
    this.doctors.set([]);
    this.emit();
  }

  protected chooseDoctor(doctor: PartnerDoctorOption): void {
    this.doctor.set(doctor);
    this.doctorTerm.set('');
    this.doctorsOpen.set(false);
    this.emit();
  }

  protected typeDoctor(value: string): void {
    this.doctorTerm.set(value);
    this.openDoctors();
  }

  protected openDoctors(): void {
    const origin = this.doctorOrigin();
    if (!origin) {
      return;
    }

    this.panelWidth.set(fieldWidth(origin));
    this.doctorsOpen.set(true);
  }

  /**
   * The box holds the query while the list is open and the answer once it is not. Unlike the booking
   * form's doctor field, a name typed here that matches nobody is not an answer: a restriction names
   * a doctor the directory holds, or it names none.
   */
  protected closeDoctors(): void {
    this.doctorTerm.set('');
    this.doctorsOpen.set(false);
  }

  protected clearDoctor(): void {
    this.doctor.set(undefined);
    this.doctorTerm.set('');
    this.emit();
  }

  protected clear(): void {
    this.partner.set(undefined);
    this.doctor.set(undefined);
    this.doctorTerm.set('');
    this.doctors.set([]);
    this.emit();
  }

  /** The doctor a stored restriction names, recognised once the partner's list has arrived. */
  private adoptInitialDoctor(doctors: PartnerDoctorOption[]): void {
    const doctorId = this.initial()?.doctorId;
    if (!doctorId || this.doctor()) {
      return;
    }

    const found = doctors.find(entry => entry.id === doctorId);
    if (found) {
      this.doctor.set(found);
    }
  }

  private emit(): void {
    const partner = this.partner();

    // A branch this block never asked about is carried through rather than dropped.
    this.changed.emit(partner ? { partnerId: partner.id, locationId: this.initial()?.locationId, doctorId: this.doctor()?.id } : undefined);
  }
}
